import { defineWorkflow } from './workflow-builder'

/**
 * WF-OFG-01 (Outbound FG Delivery Plan) — `docs/tl-doc/12B-SERVICE-DATA/JAL_12B-SERVICE-DATA-WF-OFG-01.md` §1–§8 [C].
 * Entity `delivery_plan` (ERD v010; tasking C09 menulis `fg_picking_order` — ERD menang).
 *
 * Penyimpangan (laporan 13):
 * - [R] Aktor mengikuti role lokal: Admin WH FG (008), Operator WH FG (009), Supervisor WH FG (010), Sales (012).
 *   Kadiv WH FG: BA OFG-01 hanya melihat, tetapi prototype v7.38 memberi disposisi keputusan Release setelah SLA (actor ditambahkan).
 * - Release = pengajuan (ACT_OFG_SUBMIT_RELEASE → Menunggu Supervisor) lalu Supervisor / Kadiv (setelah SLA) menyetujui
 *   (→ Released, tugas terbit) atau menolak (→ Forecast + alasan); Admin dapat menarik pengajuan. `ACT_OFG_RELEASE_ORDER`
 *   tidak lagi dipanggil handler (dipertahankan untuk kompatibilitas Workflow Studio).
 * - `ACT_OFG_START_PICK` / `ACT_OFG_COMPLETE_STAGING` dijalankan handler `pick-unit` / `stage-rack` atas nama operator
 *   pemilik tugas (Released → Staging → Staged), bukan tombol manual.
 */
export const WF_OFG = defineWorkflow({
  no: 10,
  code: 'WF-OFG-01',
  name: 'Outbound FG Delivery Plan Workflow',
  entity: 'delivery_plan',
  statuses: [
    ['OFG_FORECAST', 'Forecast', true, false, '#64748B', 'Rencana pengiriman diimpor / dibuat'],
    ['OFG_RELEASED', 'Released', false, false, '#3B82F6', 'Order dirilis, alokasi FIFO dihitung sistem'],
    ['OFG_STAGING', 'Staging', false, false, '#06B6D4', 'Operator melakukan picking unit ke staging'],
    ['OFG_STAGED', 'Staged', false, false, '#8B5CF6', 'Semua unit di ZS-01, siap dimuat ke truk'],
    ['OFG_SHIPPED', 'Shipped', false, true, '#10B981', 'Surat Jalan (SJ) terbit, truk berangkat'],
    ['OFG_ARCHIVED', 'Archived', false, true, '#94A3B8', 'Forecast kedaluwarsa tanpa dirilis'],
    // Prototype v7.38: Release = pengajuan ke Supervisor (BA 12D masih menulis Release langsung — laporan 22 K-OFG-1).
    [
      'OFG_PENDING_SPV',
      'Menunggu Supervisor',
      false,
      false,
      '#F59E0B',
      'Release diajukan Admin, menunggu keputusan Supervisor WH FG'
    ]
  ],
  actors: [
    ['ACT_OFG_ADMIN_FG', 'Admin WH FG', 'admin_wh_fg', null],
    ['ACT_OFG_OP_FG', 'Operator WH FG', 'operator_wh_fg', null],
    ['ACT_OFG_SPV_FG', 'Supervisor WH FG', 'supervisor_wh_fg', null],
    ['ACT_OFG_SALES', 'Sales / Marketing', 'sales_marketing', { id: { _null: false } }],
    ['ACT_OFG_KADIV_FG', 'Kadiv WH FG', 'kadiv_wh_fg', null]
  ],
  components: [
    ['CMP_OFG_RELEASE', 'Tombol Rilis Order', 'ACTION', { color: '#3B82F6', variant: 'primary' }],
    ['CMP_OFG_STAGING_DONE', 'Tombol Selesai Staging', 'ACTION', {}],
    ['CMP_OFG_MARK_SHIP', 'Tombol Tandai Shipped (SJ)', 'ACTION', {}],
    ['CMP_OFG_CANCEL_SJ', 'Tombol Batalkan Surat Jalan', 'ACTION', {}],
    ['CMP_OFG_ARCHIVE', 'Tombol Arsipkan Forecast', 'ACTION', {}],
    [
      'CMP_OFG_SUBMIT_RELEASE',
      'Tombol Ajukan Release ke Supervisor',
      'ACTION',
      { color: '#3B82F6', variant: 'primary' }
    ],
    ['CMP_OFG_PULL_RELEASE', 'Tombol Tarik Pengajuan Release', 'ACTION', {}],
    ['CMP_OFG_APPROVE_RELEASE', 'Tombol Setujui Release', 'ACTION', { color: '#10B981', variant: 'primary' }],
    ['CMP_OFG_REJECT_RELEASE', 'Tombol Tolak Release', 'ACTION', { color: '#EF4444', variant: 'outlined' }],
    ['CMP_OFG_PANEL_HEADER', 'Panel Header Delivery Plan', 'PANEL', {}],
    ['CMP_OFG_PANEL_ITEMS', 'Panel Rincian SKU & FIFO', 'PANEL', {}],
    ['CMP_OFG_PANEL_SHIPPING', 'Panel Ekspedisi & Surat Jalan', 'PANEL', {}]
  ],
  actions: [
    ['ACT_OFG_RELEASE_ORDER', 'OFG_FORECAST', 'OFG_RELEASED', 'CMP_OFG_RELEASE', false, false, null],
    ['ACT_OFG_START_PICK', 'OFG_RELEASED', 'OFG_STAGING', 'CMP_OFG_RELEASE', false, false, null],
    ['ACT_OFG_COMPLETE_STAGING', 'OFG_STAGING', 'OFG_STAGED', 'CMP_OFG_STAGING_DONE', false, false, null],
    [
      'ACT_OFG_DISPATCH',
      'OFG_STAGED',
      'OFG_SHIPPED',
      'CMP_OFG_MARK_SHIP',
      false,
      false,
      { is_shipped: true, shipped_at: '$NOW' }
    ],
    ['ACT_OFG_ROLLBACK_SJ', 'OFG_SHIPPED', 'OFG_STAGED', 'CMP_OFG_CANCEL_SJ', true, true, null],
    ['ACT_OFG_EXPIRE', 'OFG_FORECAST', 'OFG_ARCHIVED', 'CMP_OFG_ARCHIVE', false, false, null],
    ['ACT_OFG_SUBMIT_RELEASE', 'OFG_FORECAST', 'OFG_PENDING_SPV', 'CMP_OFG_SUBMIT_RELEASE', false, false, null],
    ['ACT_OFG_PULL_RELEASE', 'OFG_PENDING_SPV', 'OFG_FORECAST', 'CMP_OFG_PULL_RELEASE', false, false, null],
    ['ACT_OFG_APPROVE_RELEASE', 'OFG_PENDING_SPV', 'OFG_RELEASED', 'CMP_OFG_APPROVE_RELEASE', false, false, null],
    ['ACT_OFG_REJECT_RELEASE', 'OFG_PENDING_SPV', 'OFG_FORECAST', 'CMP_OFG_REJECT_RELEASE', true, true, null]
  ],
  eligibility: [
    ['ACT_OFG_RELEASE_ORDER', 'ACT_OFG_ADMIN_FG'],
    ['ACT_OFG_START_PICK', 'ACT_OFG_OP_FG'],
    ['ACT_OFG_COMPLETE_STAGING', 'ACT_OFG_OP_FG'],
    // [R] Admin menutup staging bila revisi order membuat seluruh sisa barang sudah berada di ZS-01.
    ['ACT_OFG_COMPLETE_STAGING', 'ACT_OFG_ADMIN_FG'],
    ['ACT_OFG_DISPATCH', 'ACT_OFG_ADMIN_FG'],
    ['ACT_OFG_ROLLBACK_SJ', 'ACT_OFG_ADMIN_FG'],
    ['ACT_OFG_EXPIRE', 'ACT_OFG_ADMIN_FG'],
    ['ACT_OFG_SUBMIT_RELEASE', 'ACT_OFG_ADMIN_FG'],
    ['ACT_OFG_PULL_RELEASE', 'ACT_OFG_ADMIN_FG'],
    ['ACT_OFG_APPROVE_RELEASE', 'ACT_OFG_SPV_FG'],
    // Kadiv memutuskan menggantikan Supervisor hanya setelah SLA OFG_RELEASE_ESCALATE (dijaga handler).
    ['ACT_OFG_APPROVE_RELEASE', 'ACT_OFG_KADIV_FG'],
    ['ACT_OFG_REJECT_RELEASE', 'ACT_OFG_SPV_FG'],
    ['ACT_OFG_REJECT_RELEASE', 'ACT_OFG_KADIV_FG']
  ],
  panels: [
    ['CMP_OFG_PANEL_HEADER', 'OFG_FORECAST', 'write', 'ACT_OFG_ADMIN_FG'],
    ['CMP_OFG_PANEL_HEADER', 'OFG_PENDING_SPV', 'read', null],
    ['CMP_OFG_PANEL_HEADER', 'OFG_RELEASED', 'read', null],
    ['CMP_OFG_PANEL_HEADER', 'OFG_SHIPPED', 'read', null],
    ['CMP_OFG_PANEL_ITEMS', 'OFG_FORECAST', 'write', 'ACT_OFG_ADMIN_FG'],
    ['CMP_OFG_PANEL_ITEMS', 'OFG_PENDING_SPV', 'read', null],
    ['CMP_OFG_PANEL_ITEMS', 'OFG_RELEASED', 'read', null],
    ['CMP_OFG_PANEL_SHIPPING', 'OFG_STAGED', 'write', 'ACT_OFG_ADMIN_FG'],
    ['CMP_OFG_PANEL_SHIPPING', 'OFG_SHIPPED', 'read', null]
  ],
  emailStatuses: ['OFG_RELEASED', 'OFG_SHIPPED']
})

export type OfgStatusCode = (typeof WF_OFG.spec.statuses)[number][0]
export type OfgActorCode = (typeof WF_OFG.spec.actors)[number][0]
export type OfgActionName = (typeof WF_OFG.spec.actions)[number][0]
export const buildWorkflowOfgSeed = WF_OFG.build
